import React, { useState, useEffect } from "react";
import { motion } from 'framer-motion';
import ToolProjectsTutorial from './ToolProjectsTutorial';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  Card,
  Grid,
  Container,
  Chip,
  CardContent,
  Tooltip,
  Paper,
  CardActions,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HandymanIcon from '@mui/icons-material/Handyman';
import ConstructionIcon from '@mui/icons-material/Construction';
import useAxiosAuth from '../hooks/useAxiosAuth';
import DeleteIcon from '@mui/icons-material/Delete';


const ToolProjectsPage = ({ user, onInteraction = () => {} }) => {  
  // States for managing projects, details, and search results
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState("");
  const [query, setQuery] = useState("");
  const [toolResults, setToolResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [projectAlias, setProjectAlias] = useState("");
  const [aliasError, setAliasError] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [showToolResults, setShowToolResults] = useState(true);
  
  
  // Define axios with authentication
  const axios = useAxiosAuth();
  //console.log("ToolProjectsPage mounted. User:", user);

  const defaultHeaders = {
    'Content-Type': 'application/json'
  };



useEffect(() => {
  // If we have tool results and just performed a search, expand the accordion
  if (toolResults && searchAttempted) {
    setShowToolResults(true);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [toolResults, searchAttempted]);

const fetchProjects = async () => {
  try {
    const response = await axios.get('/tool_projects', { 
      headers: defaultHeaders,
      validateStatus: function (status) {
        return (status >= 200 && status < 300) || status === 404; // Accept 404
      }
    });
    
    //console.log("Raw tool projects response:", response.data); // Debug log
    
    // Handle 404 case
    if (response.status === 404) {
      console.log("No tool projects found yet, which is expected for new users.");
      setProjects([]);
      return;
    }
    
    // Process the projects data based on your API response format
    if (response.data && response.data["tool projects"]) {
      // Based on your backend code: "tool projects": projects
      const toolProjects = response.data["tool projects"];
      //console.log("Found tool projects in response:", toolProjects);
      setProjects(toolProjects);
    } else if (response.data && response.data.tool_projects) {
      // Alternative format
      setProjects(response.data.tool_projects);
    } else {
      console.warn("Unexpected response format:", response.data);
      setProjects([]);
    }
  } catch (err) {
    console.error("Failed to fetch tool projects:", err);
    // Only set error for unexpected errors, not 404
    if (err.response?.status !== 404) {
      setError("Failed to fetch tool projects.");
    }
  }
};

  const handleInteraction = (callback) => {
    if (!user) {
      onInteraction();
      return;
    }
    callback();
  };

  useEffect(() => {
    //console.log("ToolProjectsPage received user:", user);
    if (user) {
      fetchProjects();
      fetchActiveProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);



const fetchActiveProject = async () => {
  if (!user) {
    console.error("No user is logged in.");
    return;
  }

  try {
    // Try to fetch the active project
    const response = await axios.get('/tool_projects/active', { 
      headers: defaultHeaders,
      // Add validateStatus to prevent axios from treating 404 as an error
      validateStatus: function (status) {
        return (status >= 200 && status < 300) || status === 404; // Accept 404 as a valid response
      }
    });

    // Check if response status is 404 (not found)
    if (response.status === 404) {
      console.log("No active tool project found yet, which is expected for new users.");
      setActiveProject(null);
      return;
    }

    // Process normal successful response
    const active = response.data.active_project?.topic;
    if (!active) {
      console.warn("No active tool project found from the backend.");
      setActiveProject(null);
      return;
    }

    setActiveProject(active);
    console.log("Fetched active tool project:", active);
  } catch (err) {
    console.error("Failed to fetch active tool project:", err);
    // Don't set error message for expected 404
    if (err.response?.status !== 404) {
      setError("Failed to fetch active tool project.");
    }
  }
};

  // Handle setting active project
  const handleSetActiveProject = async (projectName) => {
    handleInteraction(async () => {
      try {
        const response = await axios.post('/tool_projects/active', { project_name: projectName }, { headers: 
defaultHeaders });
        
        setActiveProject(projectName);
        
        // Handle the specific response format
        if (response.data.tool_data) {
          setToolResults(response.data.tool_data);
          setShowToolResults(false);
        } else {
          setToolResults(null);
        }

        console.log("Active tool project set successfully.");
      } catch (err) {
        console.error("Failed to set active tool project:", err);
        setError("Failed to set active tool project.");
      }
    });
  };

  const handleSearch = async () => {
    handleInteraction(async () => {
      if (!query.trim()) {
        return;
      }
      
      // Add client-side character validation
      if (query.length > 700) {
        setError("Your search query must be less than 700 characters.");
        return;
      }
      
      setLoading(true);
      setError("");
      setToolResults(null);
      setSearchAttempted(true);
      
      try {
        const response = await axios.post('/tool_search', { 
          q: query, 
          project_alias: projectAlias 
        });
        
        console.log("Tool search response:", response.data);
        
        // Handle the specific response format for tool search
        if (response.data.tool_response) {
          setToolResults(response.data.tool_response);
          setShowToolResults(true);
          
          // Show notification after search completes successfully
          setShowNotification(true);
          
          // Hide notification after 10 seconds
          setTimeout(() => {
            setShowNotification(false);
          }, 10000);
        } else {
          setToolResults(null);
        }
        
        await fetchProjects();
        
        if (response.data.new_project_name) {
          setActiveProject(response.data.new_project_name);
        }
      } catch (err) {
        console.error("Tool search error:", err);
        
        // Extract the specific error message from the API response if available
        if (err.response && err.response.data && err.response.data.detail) {
          setError(err.response.data.detail);
        } else {
          setError("Failed to complete tool search. Please try again.");
        }
        
        // Auto-hide error after 10 seconds
        setTimeout(() => {
          setError("");
        }, 10000);
      } finally {
        setLoading(false);
      }
    });
  };

  const handleDeleteProject = async (projectName) => {
    handleInteraction(async () => {
      try {
        await axios.delete(`/projects/tool/${encodeURIComponent(projectName)}`);
        
        // If the deleted project was active, clear the active project
        if (activeProject === projectName) {
          setActiveProject(null);
          setToolResults(null);
        }
        
        // Refresh the projects list
        fetchProjects();
        
      } catch (err) {
        setError(`Failed to delete tool project: ${err.response?.data?.detail || err.message}`);
      }
    });
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the DIY tool project "{projectToDelete}"? This action cannot be 
undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              handleDeleteProject(projectToDelete);
              setDeleteConfirmOpen(false);
            }} 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            width: "100%",
            maxWidth: "lg",
            mx: "auto"
          }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      <Container maxWidth="lg">
                  {/* Back to Home Link - ADD THIS CODE */}
<Box sx={{ display: 'flex', mb: 4 }}>
  <Button
    startIcon={<ArrowBackIosIcon />}
    variant="text"
    onClick={() => navigate('/')}
    sx={{
      color: '#1e3c72',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      }
    }}
  >
    Back to DIYMind Home
  </Button>
</Box>
{/* End of Back to Home Link */}
        {/* Header Section */}
        <Box 
          component={motion.div}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          sx={{ mb: 6 }}
        >
          <Typography 
            variant="h1" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              mb: 2,
               background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
              backgroundClip: 'text',
              textFillColor: 'transparent'
            }}
          >
            Find DIY Tools
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Search for the best DIY tools and equipment for your projects.
          </Typography>
          
          {/* Project Creation Form */}
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2, mb: 4 }}>
            {/* Project Name Input */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Project Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="e.g., Backyard Deck Construction"
                value={projectAlias}
                onChange={(e) => handleInteraction(() => {
                  setProjectAlias(e.target.value);
                  setAliasError(false);
                })}
                error={aliasError}
                helperText={aliasError ? "Project name is required" : ""}
              />
            </Box>
            
            {/* Search Input */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                What tools are you looking for? <span style={{ color: "red" }}>*</span>
                <Tooltip title="Describe the tools you need for your DIY project">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter your tool search query"
                value={query}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInteraction(() => {
                    setQuery(value);
                    if (error) setError("");
                  });
                }}
                multiline
                rows={2}
                error={query.length > 700}
                helperText={
                  query.length > 700 
                    ? `Character count: ${query.length}/700 (Exceeds limit)`
                    : `Character count: ${query.length}/700`
                }
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SearchIcon />}
                onClick={() => handleInteraction(handleSearch)}
                disabled={loading || query.length > 700 || !query.trim()}
                sx={{ px: 4 }}
              >
                {loading ? "Searching..." : "Search"}
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Notification Alert */}
        {showNotification && toolResults && (
          <Alert 
            severity="info" 
            sx={{ 
              mt: 2, 
              mb: 2, 
              display: 'flex', 
              alignItems: 'center',
              borderRadius: 2
            }}
          >
            <Typography variant="body2">
              Search complete! Scroll down to see tool recommendations.
            </Typography>
          </Alert>
        )}

        {/* No Results Message */}
        {searchAttempted && !loading && !toolResults && (
          <Alert
            severity="info"
            sx={{
              mt: 2,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              borderRadius: 2,
              backgroundColor: '#e3f2fd',
              color: '#0d47a1',
              border: '1px solid #bbdefb',
              '& .MuiAlert-icon': {
                color: '#1976d2'
              }
            }}
          >
            <Typography variant="body2">
              No tool recommendations found. Try a different query.
            </Typography>
          </Alert>
        )}

        {/* Active Project and Results Section */}
        <Box sx={{ mt: 4 }}>
          {/* Projects Dropdown */}
          <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom>Your DIY Tool Projects</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Select
                value={activeProject || ""}
                onChange={(e) => handleInteraction(() => handleSetActiveProject(e.target.value))}
                sx={{ flexGrow: 1, minWidth: '200px' }}
                displayEmpty
              >
                <MenuItem value="" disabled>Select a tool project</MenuItem>
                {projects.map((project) => (
                  <MenuItem 
                    key={project.name}
                    value={project.name}
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>{project.name}</span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setProjectToDelete(project.name);
                        setDeleteConfirmOpen(true);
                      }}
                      sx={{ ml: 2 }}
                    >
                      <DeleteIcon color="error" />
                    </IconButton>
                  </MenuItem>
                ))}
              </Select>
              
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleInteraction(() => {
                  setActiveProject("");
                  setProjectAlias("");
                  setQuery("");
                })}
              >
                New Tool Project
              </Button>
            </Box>
          </Paper>

{/* Tool Results Accordion */}
{toolResults && (
  <Accordion 
    expanded={showToolResults} 
    onChange={() => handleInteraction(() => setShowToolResults(!showToolResults))}
    sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}
    defaultExpanded={true} // This will make it expanded by default on new searches
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ConstructionIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          DIY Tools Recommendation
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails>
      <Paper 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        elevation={1} 
        sx={{ 
          p: 3,
          borderRadius: 2,
          background: 'white',
        }}
      >
        {/* Tool Summary */}
        {toolResults['Tool Summary'] && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#1e3c72', fontWeight: 'bold' }}>
              Project Summary
            </Typography>
            <Card sx={{ p: 2, mb: 3, borderLeft: '4px solid #1e3c72' }}>
              <Typography variant="body1">
                {toolResults['Tool Summary']}
              </Typography>
            </Card>
          </Box>
        )}
        
        {/* Tools List */}
        {toolResults.tools && toolResults.tools.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#1e3c72', fontWeight: 'bold' }}>
              Recommended Tools
            </Typography>
            <Grid container spacing={3}>
              {toolResults.tools.map((tool, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    component={motion.div}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <Box sx={{ p: 2, bgcolor: '#1e3c72', color: 'white' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                        {tool.product_name || "Tool Name"}
                      </Typography>
                    </Box>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          icon={<HandymanIcon />}
                          label={tool.tool_type || "Tool"}
                          color="primary"
                          size="small"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {tool.description || "No description available"}
                      </Typography>
                    </CardContent>
                    {tool.short_url && (
                      <CardActions>
                        <Button
                          size="small"
                          color="primary"
                          href={tool.short_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          fullWidth
                        >
                          View Tool
                        </Button>
                      </CardActions>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        
        {/* Handle case when no tools data is present */}
        {(!toolResults.tools || toolResults.tools.length === 0) && (!toolResults['Tool Summary']) && (
          <Typography variant="body1">
            No tool recommendations available. Try a different search query.
          </Typography>
        )}
        </Paper>
      </AccordionDetails>
      </Accordion>
       )}

          {/* No Results Message */}
          {searchAttempted && !loading && !toolResults && (
            <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
              No tool results found. Try a different query.
            </Typography>
          )}
        </Box>
      </Container>
       <ToolProjectsTutorial />
    </Box>
  );
};

export default ToolProjectsPage;
